import React from 'react';
import { Printer, X, Clock, Scale, Layers, TrendingUp, TrendingDown, Minus, RefreshCw, Info } from 'lucide-react';
import { API_URL } from '../../services/api';

const fmt  = (v, dec = 2) => parseFloat(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

const hToStr = (h) => {
  const totalMin = Math.round(parseFloat(h || 0) * 60);
  if (totalMin === 0) return '—';
  const heures = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  if (heures === 0) return `${min}min`;
  if (min === 0) return `${heures}h`;
  return `${heures}h${min}min`;
};

// Retourne { delta, pct, bon } — bon = true si réel < estimé (moins = mieux pour temps et coût, plus = mieux pour poids metal si proche)
const ecart = (reel, estime, moinsMieux = true) => {
  const r = parseFloat(reel || 0);
  const e = parseFloat(estime || 0);
  const d = r - e;
  const pct = e !== 0 ? (d / e) * 100 : 0;
  const bon = moinsMieux ? d <= 0 : Math.abs(d) <= e * 0.05; // pour poids : écart < 5% = ok
  return { delta: d, pct, bon, neutre: d === 0, reel: r, estime: e };
};

// Bloc de comparaison visuel
const BlochCompar = ({ titre, icon: Icon, estime, reel, unite, moinsMieux = true, formatFn = fmt, hasBaseline = true }) => {
  const e = ecart(reel, estime, moinsMieux);
  const hasData = e.estime > 0 || e.reel > 0;
  // §113d - hasBaseline=false (has_devis backend) : estimations auto-générées -> pas de verdict vert/rouge
  const verdict = hasBaseline && hasData && !e.neutre;
  const couleurBg = !verdict ? (hasData ? 'bg-gray-50 border-gray-300' : 'bg-gray-50 border-gray-200') : e.bon ? 'bg-green-50 border-green-400' : 'bg-red-50 border-red-400';
  const couleurTexte = !verdict ? (hasData ? 'text-gray-600' : 'text-gray-400') : e.bon ? 'text-green-700' : 'text-red-700';
  const couleurVal = !verdict ? (hasData ? 'text-gray-700' : 'text-gray-500') : e.bon ? 'text-green-800' : 'text-red-800';
  const IconTrend = !verdict ? Minus : e.bon ? TrendingDown : TrendingUp;

  return (
    <div className={`border-2 rounded-xl p-4 ${couleurBg}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`h-5 w-5 ${couleurTexte}`} />}
          <span className="text-xs font-bold uppercase tracking-wider text-gray-600">{titre}</span>
        </div>
        <IconTrend className={`h-5 w-5 ${couleurTexte}`} />
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-xs text-gray-500 mb-0.5">Devis prévu</div>
          <div className="text-xl font-bold text-gray-700">{hasData && e.estime > 0 ? `${formatFn(e.estime, 3)} ${unite}` : '—'}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-0.5">Réel</div>
          <div className={`text-xl font-bold ${couleurVal}`}>{hasData && e.reel > 0 ? `${formatFn(e.reel, 3)} ${unite}` : '—'}</div>
        </div>
      </div>
      {hasBaseline && hasData && e.estime > 0 && e.reel > 0 && !e.neutre && (
        <div className={`text-center text-sm font-bold px-3 py-1.5 rounded-lg ${e.bon ? 'bg-green-200 text-green-900' : 'bg-red-200 text-red-900'}`}>
          {e.delta > 0 ? '+' : ''}{formatFn(e.delta, 3)} {unite} ({e.delta > 0 ? '+' : ''}{fmt(e.pct, 1)}%)
        </div>
      )}
    </div>
  );
};

const FicheBijouTermine = ({ ordre, onClose, imagePrincipale, bijouLie, pierresOf, onRelancer, relanceLoading, relanceError }) => {
  if (!ordre) return null;

  // Detection OF retro (chantier 4)
  const _ofForRetro = ordre.ordre || ordre;
  const isOFRetro = (_ofForRetro?.numero_ordre || '').startsWith('OF-RETRO-');
  const dateRetro = _ofForRetro?.created_at || _ofForRetro?.date_commande;

  const o       = ordre.ordre || ordre;
  const etapes  = ordre.etapes || [];
  const matieres = ordre.matieres || [];
  const composants = ordre.composants || [];
  const ac      = ordre.analyse_couts || {};
  // §113d - has_devis (backend 48a44d8) : false = pas de devis baseline -> pas de code couleur "dépassement"
  const hasDevis = ordre.has_devis !== false;
  // Pierres : priorité aux pierres du devis, sinon of_pierres (réelles)
  const pierres = (ordre.pierres?.length > 0 ? ordre.pierres : pierresOf) || [];

  // Données bijou terminé (depuis l'objet bijou lié, plus fiable que l'ordre)
  const bL = bijouLie || {};

  const nom         = o.description || `${o.type_bijou || 'BIJOU'} ${o.couleur_or || ''}`.trim() || 'Bijou sans désignation';
  const reference   = bL.reference || o.ref_article || o.reference_article || o.numero_ordre || 'N/A';
  const numeroOf    = o.numero_ordre || `#${o.id}`;
  const numFab      = o.num_fab || o.id || '-';
  const idProduit   = bL.id_produit || o.id_produit || '-';
  const client      = o['c.client_nom'] || o.client_nom || bL.client_nom || 'N/A';
  const couleur     = o.couleur_or || 'JAUNE';
  const typeBijou   = o.type_bijou || 'BIJOU';
  const collection  = o.collection_nom || '';
  const metal       = `${couleur} 18K`;

  // Poids — réel = poids_metal du bijou terminé (bijouLie) ; estimé = devis
  const poidsReel   = parseFloat(bL.poids_metal || o.poids_or_reel || o.poids_metal_net || 0);
  // L'estimé vient du devis (ordre.poids_or top-level) seulement s'il est différent du réel
  const _poidsOrTopLevel = parseFloat(ordre.poids_or || 0);
  const poidsEstime = parseFloat(o.poids_or_estime || o.poids_unitaire ||
    (_poidsOrTopLevel > 0 && _poidsOrTopLevel !== poidsReel ? _poidsOrTopLevel : 0));

  // Temps — on utilise uniquement temps_estime_heures (fixé si ouvriers ajoutés au devis)
  // On n'utilise PAS ac.temps_estime_total qui somme des durées d'étapes auto-générées
  const tempsEstime = parseFloat(o.temps_estime_heures || 0);
  const tempsReel   = parseFloat(ac.temps_reel_total || o.temps_reel_heures || 0);

  // Coûts
  const coutMatEstime = parseFloat(o.cout_matieres_estime || 0);
  const coutMatReel   = parseFloat(o.cout_matieres_reel || 0);
  const coutMoEstime  = parseFloat(o.cout_main_oeuvre_estime || o.cout_main_oeuvre || 0);
  const coutMoReel    = parseFloat(o.cout_main_oeuvre_reel || 0);
  // COÛT RÉEL = ce qu'on a dépensé (sans marge), priorité sur bijouLie.cout_reel_total
  const totalReel     = parseFloat(bL.cout_reel_total || ac.cout_reel_total || (coutMatReel + coutMoReel) || 0);
  // PRIX DE VENTE = ce que le client paye (avec marge appliquée)
  const prixVente     = parseFloat(bL.prix_vente || o.prix_final || o.prix_devis || 0);
  // Coût estimé devis (sans marge)
  const totalEstime   = parseFloat(ac.cout_theorique || (coutMatEstime + coutMoEstime) || 0);

  const dateFin   = o.date_livraison_reelle || o.date_fin_production;
  const dateDebut = o.date_debut_production || o.date_commande;

  // Image
  const imgUrl = imagePrincipale || (o.image_url
    ? (o.image_url.startsWith('/') ? `${API_URL.replace('/api','')}${o.image_url.replace(/\\/g,'/')}` : o.image_url)
    : null);

  // Quantité pièces
  const qte = parseInt(o.quantite_pieces || 1);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto">

        {/* Header boutons */}
        <div className="print:hidden sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10">
          <h2 className="text-lg font-bold text-gray-800">📋 Fiche Bijou Terminé — {numeroOf}</h2>
          <div className="flex gap-2">
            {onRelancer && (
              <button
                onClick={onRelancer}
                disabled={relanceLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 text-sm"
                title="Créer un nouveau bijou + OF identiques (brouillon éditable)"
              >
                <RefreshCw className={`h-4 w-4 ${relanceLoading ? 'animate-spin' : ''}`} /> {relanceLoading ? 'Relance...' : 'Relancer une production'}
              </button>
            )}
            <button onClick={() => window.print()} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm">
              <Printer className="h-4 w-4" /> Imprimer
            </button>
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-2 text-sm">
              <X className="h-4 w-4" /> Fermer
            </button>
          </div>
        </div>

        {/* Bandeau OF retro (chantier 4) */}
        {isOFRetro && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 px-6 py-3 flex items-center gap-3">
            <Info className="h-5 w-5 text-yellow-600 flex-shrink-0" />
            <div className="text-sm text-yellow-800">
              <strong>OF reconstitué rétroactivement</strong>
              {dateRetro && <span> le {fmtDate(dateRetro)}</span>}. Étapes et durées sont des estimations par défaut.
            </div>
          </div>
        )}

        {/* Erreur relance (chantier 4) */}
        {relanceError && (
          <div className="bg-red-50 border-l-4 border-red-400 px-6 py-3 text-sm text-red-700">
            <strong>Erreur relance :</strong> {relanceError}
          </div>
        )}

        <div className="p-6 print:p-4 space-y-5">

          {/* ══ EN-TÊTE ══ */}
          <div className="flex justify-between items-start border-b-2 border-gray-800 pb-4">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Ordre de Fabrication</div>
              <div className="text-3xl font-black text-gray-900 font-mono">{numeroOf}</div>
              <div className="text-sm text-gray-500 mt-1">Fabrication N° {numFab}</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-gray-800">BOTUHA</div>
              <div className="text-sm text-gray-500">Fiche de Fabrication Terminée</div>
              {collection && <div className="text-xs text-indigo-600 font-medium mt-1">Collection {collection}</div>}
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500 uppercase">Date fin</div>
              <div className="text-lg font-bold text-gray-900">{fmtDate(dateFin)}</div>
              <div className="text-xs text-gray-400 mt-1">Début : {fmtDate(dateDebut)}</div>
            </div>
          </div>

          {/* ══ RÉFÉRENCE + INFOS BIJOU ══ */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-500 uppercase">Référence Article</div>
                  <div className="text-2xl font-black font-mono text-gray-900">{reference}</div>
                  {idProduit !== '-' && (
                    <div className="mt-1">
                      <span className="text-xs text-amber-600 font-bold uppercase">ID Pièce : </span>
                      <span className="font-mono font-bold text-amber-800">{idProduit}</span>
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase">Bijou</div>
                  <div className="font-bold text-gray-800">{typeBijou} — {metal}</div>
                  <div className="text-sm text-gray-600 mt-1 line-clamp-2">{nom}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase">Client</div>
                  <div className="font-bold text-gray-800">{client}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase">Quantité</div>
                  <div className="text-2xl font-black text-gray-900">{qte} <span className="text-sm font-normal text-gray-500">pièce{qte > 1 ? 's' : ''}</span></div>
                </div>
              </div>
              {o.notes && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
                  <span className="font-bold">⚠ Note : </span>{o.notes}
                </div>
              )}
            </div>
            {/* Photo bijou */}
            <div className="border border-gray-300 rounded-xl flex items-center justify-center min-h-[140px] overflow-hidden bg-gray-50">
              {imgUrl ? (
                <img src={imgUrl} alt="Bijou" className="max-w-full max-h-[160px] object-contain p-2"
                  onError={e => { e.target.style.display='none'; }} />
              ) : (
                <div className="text-center text-gray-300 p-4">
                  <div className="text-5xl">💍</div>
                  <div className="text-xs mt-2">Pas de photo</div>
                </div>
              )}
            </div>
          </div>

          {/* ══ BLOCS DE COMPARAISON COLORÉS ══ */}
          <div>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> Analyse — Devis vs Réel
              {hasDevis ? (
                <>
                  <span className="text-green-600 ml-2">■ Sous le devis (bon)</span>
                  <span className="text-red-600 ml-2">■ Dépassement (attention)</span>
                </>
              ) : (
                <span className="text-gray-400 ml-2 normal-case font-normal">(pas de devis de référence — valeurs indicatives)</span>
              )}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <BlochCompar
                titre="Poids Or"
                icon={Scale}
                estime={poidsEstime}
                reel={poidsReel}
                unite="g"
                moinsMieux={false}
                formatFn={(v) => fmt(v, 3)}
                hasBaseline={hasDevis}
              />
              <BlochCompar
                titre="Temps travail"
                icon={Clock}
                estime={tempsEstime}
                reel={tempsReel}
                unite=""
                moinsMieux={true}
                formatFn={(v) => hToStr(v)}
                hasBaseline={hasDevis}
              />
              {(coutMatEstime > 0 || coutMatReel > 0) && (
                <BlochCompar
                  titre="Coût matières"
                  icon={Layers}
                  estime={coutMatEstime}
                  reel={coutMatReel}
                  unite="DH"
                  moinsMieux={true}
                  hasBaseline={hasDevis}
                />
              )}
              {(totalEstime > 0 || totalReel > 0) && (
                <BlochCompar
                  titre="Coût total"
                  icon={TrendingUp}
                  estime={totalEstime}
                  reel={totalReel}
                  unite="DH"
                  moinsMieux={true}
                  hasBaseline={hasDevis}
                />
              )}
            </div>
          </div>

          {/* ══ ÉTAPES AVEC COMPARAISON ══ */}
          <div className="border border-gray-300 rounded-xl overflow-hidden">
            <div className="bg-gray-800 text-white px-4 py-2.5 font-bold text-sm flex items-center gap-2">
              <Clock className="h-4 w-4" /> Étapes de Fabrication — Comparaison Temps
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-xs font-bold text-gray-600 uppercase">
                    <th className="px-3 py-2 text-left">Étape</th>
                    <th className="px-3 py-2 text-left">Ouvrier</th>
                    <th className="px-3 py-2 text-center">Prévu</th>
                    <th className="px-3 py-2 text-center">Réel</th>
                    <th className="px-3 py-2 text-center">Écart</th>
                    <th className="px-3 py-2 text-center">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {etapes.length > 0 ? etapes.map((etape, i) => {
                    const rH = parseFloat(etape.duree_reelle_heures || 0);
                    const eH = parseFloat(etape.duree_estimee_heures || 0);
                    const e = ecart(rH, eH, true);
                    const done = etape.statut === 'terminee' || etape.statut === 'termine' || rH > 0;
                    // §113d - sans devis baseline : pas de vert/rouge sur les lignes
                    const rowBg = done ? (hasDevis ? (e.bon || e.neutre ? 'bg-green-50' : 'bg-red-50') : 'bg-gray-50') : 'bg-white';
                    return (
                      <tr key={i} className={`border-b border-gray-100 ${rowBg}`}>
                        <td className="px-3 py-2 font-medium">{etape.nom_etape || etape.nom || `Étape ${i+1}`}</td>
                        <td className="px-3 py-2 text-gray-600">{etape.ouvrier_nom || <span className="text-gray-400 italic text-xs">Non assigné</span>}</td>
                        <td className="px-3 py-2 text-center text-gray-500">{eH > 0 ? hToStr(eH) : '—'}</td>
                        <td className="px-3 py-2 text-center font-bold">{rH > 0 ? hToStr(rH) : '—'}</td>
                        <td className="px-3 py-2 text-center">
                          {hasDevis && rH > 0 && eH > 0 && !e.neutre ? (
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${e.bon ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                              {e.delta > 0 ? '+' : ''}{hToStr(Math.abs(e.delta))}
                            </span>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                            done ? 'bg-green-100 text-green-800'
                            : etape.statut === 'en_cours' ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-500'
                          }`}>
                            {done ? '✓ Terminé' : etape.statut === 'en_cours' ? '⏳ En cours' : '⏸ En attente'}
                          </span>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan="6" className="px-4 py-6 text-center text-gray-400 italic text-xs">Aucune étape enregistrée</td></tr>
                  )}
                </tbody>
                {/* Totaux */}
                {(tempsEstime > 0 || tempsReel > 0) && (() => {
                  const eTotal = ecart(tempsReel, tempsEstime, true);
                  return (
                    <tfoot>
                      <tr className={`border-t-2 border-gray-400 font-bold ${!hasDevis ? 'bg-gray-100' : eTotal.bon || eTotal.neutre ? 'bg-green-100' : 'bg-red-100'}`}>
                        <td colSpan="2" className="px-3 py-2.5">TOTAL</td>
                        <td className="px-3 py-2.5 text-center text-gray-600">{hToStr(tempsEstime)}</td>
                        <td className="px-3 py-2.5 text-center">{hToStr(tempsReel)}</td>
                        <td className={`px-3 py-2.5 text-center ${!hasDevis ? 'text-gray-500' : eTotal.bon ? 'text-green-800' : 'text-red-800'}`}>
                          {hasDevis && !eTotal.neutre ? `${eTotal.delta > 0 ? '+' : ''}${hToStr(Math.abs(eTotal.delta))} (${eTotal.delta > 0 ? '+' : ''}${fmt(eTotal.pct, 0)}%)` : hasDevis ? '=' : '—'}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  );
                })()}
              </table>
            </div>
          </div>

          {/* ══ IMAGE + MATIÈRES ══ */}
          <div className="grid grid-cols-2 gap-4">
            {/* Matières */}
            <div className="border border-gray-300 rounded-xl overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 font-bold text-sm border-b border-gray-300">Matières Premières</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs text-gray-500">
                    <th className="px-3 py-1.5 text-left">Réf.</th>
                    <th className="px-3 py-1.5 text-center">Qté</th>
                    <th className="px-3 py-1.5 text-right">Prix/u</th>
                    <th className="px-3 py-1.5 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {matieres.length > 0 ? matieres.map((m, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="px-3 py-1.5 font-mono text-xs">{m.reference || m.designation || m.type_matiere || '-'}</td>
                      <td className="px-3 py-1.5 text-center">{fmt(m.quantite_grammes || m.quantite_prevue || m.quantite, 3)} g</td>
                      <td className="px-3 py-1.5 text-right">{fmt(m.prix_achat_unitaire || m.prix_unitaire)} DH</td>
                      <td className="px-3 py-1.5 text-right font-medium">{fmt(m.prix_total || m.cout_total)} DH</td>
                    </tr>
                  )) : (
                    <tr><td colSpan="4" className="px-4 py-4 text-center text-gray-400 italic text-xs">Aucune matière</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pierres */}
            <div className="border border-gray-300 rounded-xl overflow-hidden">
              <div className="bg-blue-50 px-4 py-2 font-bold text-sm border-b border-blue-200 text-blue-800">Pierres</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs text-gray-500">
                    <th className="px-3 py-1.5 text-left">Type</th>
                    <th className="px-3 py-1.5 text-center">Qté</th>
                    <th className="px-3 py-1.5 text-center">Carats</th>
                  </tr>
                </thead>
                <tbody>
                  {pierres.length > 0 ? pierres.map((p, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="px-3 py-1.5">
                        <div>{p.type_pierre || p.designation || p.type_matiere || '-'}</div>
                        {(p.reference_pierre || p.reference) && <div className="text-xs text-gray-400 font-mono">{p.reference_pierre || p.reference}</div>}
                      </td>
                      <td className="px-3 py-1.5 text-center">{p.quantite || p.nb_pierres || 1}</td>
                      <td className="px-3 py-1.5 text-center">{fmt(p.poids_carats || p.poids_total_carats, 3)} ct</td>
                    </tr>
                  )) : (
                    <tr><td colSpan="3" className="px-4 py-4 text-center text-gray-400 italic text-xs">Aucune pierre</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ══ COMPOSANTS ══ */}
          {composants.length > 0 && (
            <div className="border border-gray-300 rounded-xl overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 font-bold text-sm border-b border-gray-300">Composants</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs text-gray-500">
                    <th className="px-3 py-1.5 text-left">Réf.</th>
                    <th className="px-3 py-1.5 text-left">Libellé</th>
                    <th className="px-3 py-1.5 text-center">Qté</th>
                    <th className="px-3 py-1.5 text-center">Poids</th>
                    <th className="px-3 py-1.5 text-left">Info</th>
                    <th className="px-3 py-1.5 text-left">Empl.</th>
                  </tr>
                </thead>
                <tbody>
                  {composants.map((c, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="px-3 py-1.5 font-mono text-xs">{c.reference || c.type || '-'}</td>
                      <td className="px-3 py-1.5">{c.nom || c.designation || '-'}</td>
                      <td className="px-3 py-1.5 text-center">{c.quantite_utilisee || c.quantite || '-'}</td>
                      <td className="px-3 py-1.5 text-center">{c.poids ? `${c.poids}g` : '-'}</td>
                      <td className="px-3 py-1.5 text-xs text-gray-500">{c.info || '-'}</td>
                      <td className="px-3 py-1.5 text-xs">{c.emplacement || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ══ SYNTHÈSE FINANCIÈRE ══ */}
          {prixVente > 0 && (
            <div className="border-2 border-yellow-400 rounded-xl overflow-hidden">
              <div className="bg-yellow-400 px-4 py-2 font-bold text-sm text-yellow-900">Synthèse Financière</div>
              <div className="grid grid-cols-3 divide-x divide-yellow-200">
                <div className="p-4 text-center">
                  <div className="text-xs text-gray-500 uppercase mb-1">Coût réel total</div>
                  <div className="text-2xl font-black text-gray-800">{fmt(totalReel)} <span className="text-sm font-normal">DH</span></div>
                </div>
                <div className="p-4 text-center">
                  <div className="text-xs text-gray-500 uppercase mb-1">Prix de vente</div>
                  <div className="text-2xl font-black text-yellow-700">{fmt(prixVente)} <span className="text-sm font-normal">DH</span></div>
                </div>
                <div className={`p-4 text-center ${prixVente - totalReel > 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div className="text-xs text-gray-500 uppercase mb-1">Marge</div>
                  <div className={`text-2xl font-black ${prixVente - totalReel > 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {fmt(prixVente - totalReel)} <span className="text-sm font-normal">DH</span>
                  </div>
                  {totalReel > 0 && <div className={`text-xs font-bold mt-1 ${prixVente - totalReel > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ({fmt((prixVente - totalReel) / totalReel * 100, 1)}%)
                  </div>}
                </div>
              </div>
            </div>
          )}

          {/* Pied de page */}
          <div className="pt-3 border-t border-gray-200 text-xs text-gray-400 flex justify-between">
            <span>{reference} — {numeroOf} — {client}</span>
            <span>Généré le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR')}</span>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          .fixed.inset-0 { position: static; }
          .fixed.inset-0, .fixed.inset-0 * { visibility: visible; }
          .fixed.inset-0 { position: absolute; left: 0; top: 0; width: 100%; height: auto; background: white; overflow: visible; }
          .print\\:hidden { display: none !important; }
          @page { margin: 1cm; size: A4; }
        }
      `}</style>
    </div>
  );
};

export default FicheBijouTermine;
